import React, { useEffect } from "react";
import { AxiosInstance } from "axios";
import { AuthState } from "../providers/AuthStateProvider";
import { AuthModule } from "../providers/AuthProvider";
import { toast } from "react-toastify";


let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

export const useApiInterceptors = (
  instance: AxiosInstance,
  authState: AuthState,
  logout: () => void,
  refreshTokens: () => Promise<string | null | undefined>,
  pathname: string
) => {
  const [currentNumOfApiCalls, setCurrentNumOfApiCalls] =
    React.useState<number>(0);
  const requestInterceptor = React.useRef<number>(0);
  const responseInterceptor = React.useRef<number>(0);

  const handleSetupInterceptors = React.useCallback(() => {
    requestInterceptor.current = instance.interceptors.request.use((config) => {
      setCurrentNumOfApiCalls((prev) => prev + 1);
      config.headers["X-App"] = `App`;
 
      const token = authState.authRef.current?.token?.token;
      
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      } 
      return config;
    });

    responseInterceptor.current = instance.interceptors.response.use(
      (response) => {
        setCurrentNumOfApiCalls((prev) => Math.max(0, prev - 1));
        return response;
      },
      async (error) => {
        const originalRequest = error.config;
        if (originalRequest.url.includes("Auth/RefreshToken")) {
          setCurrentNumOfApiCalls(0);
          logout();
        }

        if (
          error.response?.status === 401 &&
          !originalRequest._retry &&
          !originalRequest.url.includes("Auth/RefreshToken")
        ) {
          originalRequest._retry = true;

          if (isRefreshing) {
            return new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject });
            })
              .then((token) => {
                setCurrentNumOfApiCalls((prev) => Math.max(0, prev - 1));
                if (token) {
                  originalRequest.headers["Authorization"] = `Bearer ${token}`;
                }
                return instance(originalRequest);
              })
              .catch((err) => {
                setCurrentNumOfApiCalls(0);
                return Promise.reject(err);
              });
          }

          isRefreshing = true;

          try {
            const newAccessToken = await refreshTokens();

            if (!newAccessToken) {
              setCurrentNumOfApiCalls(0);
              processQueue(new Error("Refresh token failed"));
              logout();
              toast.error("Please sign in again, your session has expired");
              return Promise.reject(
                new Error("Token refresh failed: Invalid refresh token")
              );
            }

            processQueue(null, newAccessToken);
            originalRequest.headers[
              "Authorization"
            ] = `Bearer ${newAccessToken}`;
            setCurrentNumOfApiCalls((prev) => Math.max(0, prev - 1));
            return instance(originalRequest);
          } catch (refreshError) {
            setCurrentNumOfApiCalls(0);
            processQueue(refreshError as Error);
            return Promise.reject(refreshError);
          } finally {
            setCurrentNumOfApiCalls((prev) => Math.max(0, prev - 1));
            isRefreshing = false;
          }
        }

        setCurrentNumOfApiCalls((prev) => Math.max(0, prev - 1));
        toast.error(error.response?.data?.message || "Internal Server Error");
        return Promise.reject(error);
      }
    );
  }, [
    setCurrentNumOfApiCalls,
    authState.authRef.current?.token?.token,
    authState.authRef.current?.refreshToken?.token,
  ]);

  useEffect(() => {
    handleSetupInterceptors();

    return () => {
      instance.interceptors.request.eject(requestInterceptor.current);
      instance.interceptors.response.eject(responseInterceptor.current);
    };
  }, [handleSetupInterceptors]);

  return currentNumOfApiCalls;
};
